import { createSlice } from '@reduxjs/toolkit';

/**
 * Initial state for the Builder store
 * @typedef {Object} BuilderState
 * @property {string|null} endpoint - API endpoint for builder operations
 * @property {string|null} mode - Builder mode template|widget
 * @property {string} client - Client type, either 'browser' or 'inbox'
 * @property {Array} components - Array of builder components
 * @property {Array} campaigns - Array of campaigns
 * @property {Array} lists - Array of lists
 * @property {*} curElement - Currently selected element
 * @property {*} prevElement - Previously selected element
 * @property {*} curRule - Current rule being applied
 * @property {Array} ids - Array of element IDs
 * @property {Array} runTime - Runtime data array
 * @property {Object} perxi - Perxi configuration object
 * @property {*} perxi.element - Perxi element
 * @property {*} perxi.rule - Perxi rule
 * @property {Object} perxi.props - Perxi properties
 * @property {Object} bank - Component bank configuration
 * @property {Array} bank.allData - All available data in the bank
 * @property {Array} bank.filteredData - Filtered data based on current scope
 * @property {string} bank.scope - Current scope filter (default: "all")
 * @property {string} bank.type - Type of bank items (default: "Modules")
 * @property {Object} copiedCss - Copied CSS styles object
 * @property {Array} rewards - Array of rewards
 * @property {*} settings - Builder settings
 * @property {*} form - Form data
 * @property {Object} formState - Form state configuration
 * @property {*} formState.wrapper - Form wrapper element
 * @property {string|null} formState.uuid - Unique identifier for the form
 * @property {string} formState.type - Form type (default: 'splash')
 * @property {Array} formState.stru - Structure array
 * @property {Array} formState.lnks - Links array
 * @property {*} formState.dcss - Desktop CSS
 * @property {*} formState.mcss - Mobile CSS
 * @property {*} formState.ccss - Custom CSS
 * @property {string} formState.name - Form name
 * @property {string} formState.preheader - Email preheader text
 * @property {string} formState.subject - Email subject line
 * @property {string} formState.body - Form body content
 * @property {*} formState.builder - Builder instance reference
 * @property {number} formState.width - Form width in pixels (default: 600)
 * @property {Object} meta - Metadata configuration
 * @property {string} meta.active - Currently active tab (default: 'settings')
 * @property {string} meta.grid - Grid visibility state (default: 'show')
 * @property {string} meta.view - Current view mode (default: 'desktop')
 * @property {number|null} currentSectionIndex - Index of the currently selected section
 */
const initialState = {
    endpoint: null,
    mode: null,
    client: 'browser', 
    components:[],
    campaigns: [],
    lists:[],
    curElement: null,
    prevElement: null,
    curRule: null,
    ids: [],
    runTime: [],
    perxi:{
        element: null,
        rule: null,
        props: {},
    },
    bank: {
        allData: [],
        filteredData: [],
        scope: "all",
        type: "Modules"
    },
    copiedCss: {},
    rewards: [],
    settings: null,
    form: null,
    formState:{
        wrapper: null,
        uuid: null,
        type: 'splash',
        stru: [],
        lnks: [],
        dcss: null,
        mcss: null,
        ccss: null,
        name: '',
        preheader: '',
        subject: '',
        body: '',
        builder:null,
        width:600
    },
    meta: {
        active: 'settings',
        grid: 'show',
        view: 'desktop'
    },
    currentSectionIndex: null
};

const builderSlice = createSlice({
    name: "pkzbuilder",
    initialState,
    reducers: {
        setClient(state, action) {
            state.client = action.payload;
        },        
        fillCampaigns(state, action) {
            state.campaigns = action.payload;
        },
        fillRewards(state, action) {
            const extras = Array.isArray(action.payload) ? action.payload : [];
            state.rewards = [
                // { value: 'main_prize', label: 'Main Prize' },
                { value: 'reward_list', label: 'All Prizes' },
                { value: 'reward_next', label: 'Next Unlock' },
                ...extras
            ];
        },        
        fuse(state, action) {
            state.lists = action.payload.lists;
        },
        fuseAny(state, action) {
            const {property, value, bmux} = action.payload;

            if(undefined !== property){
                state[property] = value;
            }

            if(undefined !== bmux) {
                $(`.${bmux}`).each(function(){
                    $(this).removeClass("active");
                    if($(this).hasClass(`state-${value}`)) {
                        $(this).addClass("active");
                    }
                });
            }
        },
        pushID: (state, action) => {
			state.ids.push(action.payload);
		},
        resetID: (state) => {
            state.ids = [];
        },
        setPerxiRuleLastUpdated: (state, action) => {
            state.runTime.push(action.payload);
        },
        setPerxiElement: (state, action) => {
            state.perxi.element = action.payload;
        },
        setPerxiLastActiveElement: (state, action) => {
            state.perxi.prevElement = action.payload;
        },        
        setPerxiRule: (state, action) => {
            let composerId, selector;
            const wrapperPattern = /^bmux-perxi-wrapper-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
            state.perxi.rule = action.payload;
            if (wrapperPattern.test(action.payload)) {
                composerId = 'perxi-composer-common';
                selector = `.${action.payload}`;;
            } else {
                composerId = document.querySelector(".perxi-sm") ? 'perxi-composer-mobile' : 'perxi-composer-desktop';
                selector = composerId === 'perxi-composer-mobile' ? 
                    `.perxi-sm .${action.payload}` : 
                    `.${action.payload}`;
            }            
            const styleSheet = document.getElementById(composerId).sheet.cssRules;    

            for (let i = 0; i < styleSheet.length; i++) {   
                const rule = styleSheet[i];
        
                if (rule.selectorText === selector) {
                    const cssString = rule.cssText,
                    cssRules = cssString.split('}')[0].split('{'),
                    rules = cssRules[1].trim(),
                    rulesSplit = rules.split(';'),
                    rulesObj = {};
                    for (let i = 0; i < rulesSplit.length - 1; i++) {
                        let keyValSplit = rulesSplit[i].split(':');
                        rulesObj[ keyValSplit[0].trim() ] = keyValSplit[1].trim();
                    }
                    state.perxi.props = rulesObj;                    
                    break;
                }
            }            
        },
        setPerxiProps: (state, action) => {
            const cssString = action.payload,
            cssRules = cssString.split('}')[0].split('{'),
            rules = cssRules[1].trim(),
            rulesSplit = rules.split(';'),
            rulesObj = {};
            for (let i = 0; i < rulesSplit.length - 1; i++) {
                let keyValSplit = rulesSplit[i].split(':');
                rulesObj[ keyValSplit[0].trim() ] = keyValSplit[1].trim();
            }
            state.perxi.props = rulesObj;
        },
        setCopiedCss: (state, action) => {
            state.copiedCss = action.payload;
        },
        setActiveAccordian: (state, action) => {
            state.perxi.activeAccordian = action.payload;
        },
        setToolBarComponents: (state, action) => {
            state.components = action.payload;
        },
        setEndPoint: (state, action) => {
            state.endpoint = action.payload;
        },
        setMode: (state, action) => {
            state.mode = action.payload;
        },
        setPaintState: (state, action) => {
            state.formState.wrapper = action.payload?.wrapper; 
            state.formState.uuid = action.payload?.uuid; 
            state.formState.type = action.payload?.type; 
            state.formState.stru = action.payload?.stru; 
            state.formState.dcss = action.payload?.dcss; 
            state.formState.mcss = action.payload?.mcss; 
            state.formState.ccss = action.payload?.ccss; 
            state.formState.lnks = action.payload?.lnks;       
            state.formState.body = action.payload?.body;       
            state.settings = action.payload?.settings;
            state.form = action.payload?.form;
        },
        fuseFormState(state, action) {
            const {property, value} = action.payload;
            if(undefined !== property){
                state.formState[property] = value;
            }
        },        
        setGridLines: (state, action) => {
            state.meta.grid = action.payload;
            state.meta.active = 'gridlines';
        },
        setViewPort: (state, action) => {
            state.meta.view = action.payload;
            state.meta.active = 'view';
        },
        setMeta: (state, action) => {
            state.meta.active = action.payload;
        },
        updateCurrentSectionIndex: (state, action) => {
            state.currentSectionIndex = action.payload;
        },

        updateNavBar(state, action) {
            const { id, updatedMenus } = action.payload;
            if (!state.settings) {
                state.settings = {}; 
            }
            if (!Array.isArray(state.settings.navbars)) {
                state.settings.navbars = [];
            }
            const navbar = state.settings.navbars.find(navbar => navbar.id === id);
            if (navbar) {
                navbar.menus = updatedMenus;
            } else {
                state.settings.navbars.push({ id, menus: updatedMenus });
            }
        },
        addMenuToNavBar(state, action) {
            const { id, menu } = action.payload;
            if (!state.settings) {
                state.settings = {}; 
            }
            if (!Array.isArray(state.settings.navbars)) {
                state.settings.navbars = [];
            }
            const navbar = state.settings.navbars.find(navbar => navbar.id === id);
            if (navbar) {
                if (!Array.isArray(navbar.menus)) {
                    navbar.menus = [];
                }
                navbar.menus.push(menu);
            }
        },
        addNewNavBar(state, action) {
            if (!state.settings) {
                state.settings = {};
            }
        
            if (typeof state.settings === 'string') {
                try {
                    state.settings = JSON.parse(state.settings);
                } catch (e) {
                    console.error('Failed to parse settings:', state.settings);
                    state.settings = {}; // fallback
                }
            }
        
            if (!Array.isArray(state.settings.navbars)) {
                state.settings.navbars = [];
            }
            const existingNavbars = [...state.settings.navbars];
            const navbarExists = existingNavbars.some(
                navbar => navbar.id === action.payload.id
            );
        
            if (!navbarExists) {
                state.settings.navbars.push(action.payload);
            }           
        },

        setSettingProp: (state, action) => {
            const { element, ...rest } = action.payload;
            if (!state.settings) {
                state.settings = {}; 
            }
            if (typeof state.settings === 'string') {
                state.settings = JSON.parse(state.settings);
            }
            if (!Array.isArray(state.settings[element])) {
                state.settings[element] = [];
            }            

            const existingElements = [...state.settings[element]];
            const elementExists = existingElements.some(item => item.id === action.payload.id);

            //don't push element, push all others
            if (!elementExists) {
                state.settings[element].push(rest);
                return;
            }          
            
            const existingIndex = existingElements.findIndex(item => item.id === action.payload.id);            
            state.settings[element][existingIndex] = {
                ...state.settings[element][existingIndex],
                ...rest
            };            

        },
        unsetSettingProp: (state, action) => {
            const { element } = action.payload;

            if (!state.settings) {
                state.settings = {}; 
            }  

            if (!Array.isArray(state.settings[element])) {
                state.settings[element] = [];
            }             

            const { key, index } = action.payload;
            if (Array.isArray(state.settings[key])) {
                if (typeof index === "number") {
                    state.settings[key].splice(index, 1);
                }
                else {
                    delete state.settings[key];
                }
            } else {
                delete state.settings[key];
            }
        },
        setFormProp: (state, action) => {
            const defaultValues = {
                bind: "",
                type: "text",
                placeholder: "New Field",
                label: "New Field",
                width: "12",
                required: false,
                integration: false,
                prechecked: false,
                desc: false
            };

            const existingIndex = state.form.findIndex(item => item.id === action.payload.id);
            // If the item already exists, update it
            if (existingIndex !== -1) {

                const existingItem = state.form[existingIndex];
                const mergedValues = Object.keys(defaultValues).reduce((acc, key) => {
                    // Priority order:
                    // 1. Explicitly provided payload value
                    // 2. Existing state value (if different from default)
                    // 3. Default value
                    if (action.payload[key] !== undefined) {
                        acc[key] = action.payload[key];
                    } else if (existingItem[key] !== defaultValues[key]) {
                        acc[key] = existingItem[key];
                    } else {
                        acc[key] = defaultValues[key];
                    }
                    return acc;
                }, {});   

                mergedValues.id = action.payload.id;    
                state.form[existingIndex] = mergedValues;         
            } else {
                const newItem = { 
                    ...defaultValues, 
                    ...action.payload 
                };
                state.form.push(newItem);
            }            
        },
        reorderForm: (state, action) => {
            const { oldIndex, newIndex } = action.payload;
            const formFields = [...state.form];
            const [movedField] = formFields.splice(oldIndex, 1);
            formFields.splice(newIndex, 0, movedField);
            state.form = formFields;
        },        
        unsetFormProp: (state, action) => {
            const { id } = action.payload;
            state.form = state.form.filter(field => field.id !== id);
        },
        setBank: (state, action) => {
            const {type, data} = action.payload;

            state.bank.allData = data;
            state.bank.filteredData = data; 
            state.bank.type = type;
        },
        filterBank: (state, action) => {
            state.bank.scope = action.payload;
            switch (action.payload) {
                case "global":
                state.bank.filteredData = state.bank.allData.filter((item) => item.scope === "global");
                break;
                case "user":
                state.bank.filteredData = state.bank.allData.filter((item) => item.scope === "user");
                break;
                default:
                state.bank.filteredData = state.bank.allData;
            }
        },
        deleteFromBank: (state, action) => {
            const idToDelete = action.payload; 
            
            state.bank.allData = state.bank.allData.filter(item => item.id !== idToDelete);

            state.bank.filteredData = state.bank.filteredData.filter(item => item.id !== idToDelete);
        },

    }
});


export const { 
    fillCampaigns, 
    fuse, 
    fuseAny, 
    pushID, 
    resetID, 

    setPerxiElement, 
    setPerxiRule, 
    setPerxiProps,
    setActiveAccordian,

    setToolBarComponents, 
    setCopiedCss,
    setEndPoint,
    fuseFormState,
    setGridLines,
    setViewPort,
    setMeta,
    setPerxiRuleLastUpdated,
    updateCurrentSectionIndex,

    setBank,
    filterBank,
    deleteFromBank
} = builderSlice.actions;

export default builderSlice.reducer;
export { builderSlice };